import { Box, Typography, makeStyles } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import * as React from "react";

import { Agent } from "@/api/generated";
import theme from "@/theme";
import { hasUsableAgentId } from "@/utilities/agentId";
import { buildAgentName } from "@/utilities/agentName";
import { AVATAR_BORDER_COLOR, MATCH_LABEL_COLOR, MATCH_SCORE_COLOR } from "@/utilities/compatibilityColors";
import { isWithinOneMonth } from "@/utilities/isWithinOneMonth";

import CertificationIcon from "@assets/image/certification_icon.png";
import NewIcon from "@assets/image/new_icon.png";
import NoImagePath from "@assets/image/NoImage.svg";

import { StatusBadge } from "../Badge";
import { CustomButton } from "../Button";
import { Chevron } from "../Chevron";
import {
  CompatibilityRadarChart,
  hasCompatibilityRadarScores,
} from "../compatibility_radar_chart/CompatibilityRadarChart";
import { CompatibilityRadarInfoButton } from "../compatibility_radar_chart/CompatibilityRadarInfoModal";
import { CertifiedPlannerBadge } from "../planner_onboarding/CertifiedPlannerBadge";

import { AgentBeginnerMessage } from "./AgentBeginnerMessage";
import { CompatibilityScoreDisplay } from "./CompatibilityScoreDisplay";

const DEFAULT_EXPAND_LABEL = "相性がよい理由を見る";

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey[300]}`,
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.5),
  },
  clickableCard: {
    cursor: "pointer",
  },
  // バッジを出すときだけカードをオーバーレイの基準にする（出さない画面の見た目に影響させないため）
  badgeAnchor: {
    position: "relative", // オーバーレイ要素の基準
    overflow: "visible", // 上端からのはみ出しを許可
  },
  topLeftBar: {
    position: "absolute",
    top: 4,
    left: 8,
    display: "flex",
    alignItems: "center",
    zIndex: 200,
    pointerEvents: "none", // バッジ上のタップもカード遷移に通す
    "& > img": {
      height: 20,
    },
    "& > img + img": {
      marginLeft: 4, // 並べたときの間隔
    },
  },
  headerGroup: { display: "flex", flexDirection: "column", gap: 8 },
  searchMeta: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
  searchBadges: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 },
  searchScore: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, marginLeft: "auto" },
  searchScoreLabel: { fontSize: 12, lineHeight: 1.5, color: theme.palette.text.secondary },
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    border: `1px solid ${AVATAR_BORDER_COLOR}`,
    objectFit: "cover",
    flexShrink: 0,
  },
  nameArea: {
    flexGrow: 1,
    minWidth: 0,
  },
  companyLogo: {
    height: 24,
    maxWidth: 120,
    objectFit: "contain",
    display: "block",
    marginBottom: theme.spacing(0.5),
  },
  companyLogoScoreRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 8,
    marginBottom: 4,
    "& $companyLogo": { minWidth: 0, marginRight: "auto", marginBottom: 0 },
  },
  name: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  // 旧バリアントは最下段、紹介・検索一覧では名前の下に会社名を表示する。
  companyName: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.5),
  },
  matchScoreArea: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  matchScoreBox: {
    textAlign: "center",
  },
  matchScoreValue: {
    fontSize: 32,
    fontWeight: 700,
    color: MATCH_SCORE_COLOR,
    lineHeight: 1,
  },
  matchScoreLabel: {
    fontSize: 12,
    lineHeight: 1.5,
    color: MATCH_LABEL_COLOR,
  },
  helpIcon: {
    fontSize: 16,
    color: MATCH_LABEL_COLOR,
    cursor: "pointer",
  },
  divider: {
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    margin: theme.spacing(2, 0),
  },
  commentLabelBox: {
    border: `1px solid ${theme.palette.primary.main}`,
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(0.5, 1.25),
    marginBottom: theme.spacing(1),
  },
  commentLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.primary.main,
  },
  commentBody: {
    fontSize: 14,
    lineHeight: 1.5,
    letterSpacing: "0.05em",
  },
  // 折りたたみ時のみ4行でクランプ。展開リンクでの展開時に全文表示する
  commentBodyClamped: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    WebkitLineClamp: 4,
  },
  // 折りたたみ時: レーダー上部だけを見せて白グラデーションでフェードするティザー表示
  radarTeaser: {
    position: "relative",
    height: 120,
    overflow: "hidden",
    marginTop: theme.spacing(2),
  },
  radarTeaserFade: {
    position: "absolute",
    inset: 0,
    background: `linear-gradient(to bottom, ${alpha(theme.palette.common.white, 0)} 0%, ${alpha(theme.palette.common.white, 0.63)} 45%, ${theme.palette.common.white} 73%)`,
  },
  radarTeaserLink: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 40,
  },
  radarExpanded: {
    marginTop: theme.spacing(2),
  },
  toggleLink: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.5),
    height: 40,
    cursor: "pointer",
  },
  toggleLinkText: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.05em",
    color: theme.palette.primary.main,
  },
  toggleLinkIcon: {
    fontSize: 24,
    color: theme.palette.primary.main,
  },
  ctaWrapper: {
    marginTop: theme.spacing(2),
  },
  buttonSpacer: {
    width: 20,
    flexShrink: 0,
  },
  buttonText: {
    flexGrow: 1,
    fontSize: "inherit",
    fontWeight: "inherit",
    lineHeight: "inherit",
    textAlign: "center",
  },
  introduction: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    border: 0,
    borderRadius: 16,
    boxShadow: `0 2px 8px ${alpha(theme.palette.semantic["text/primary"], 0.06)}`,
    marginBottom: 0,
    color: theme.palette.semantic["text/primary"],
    "&:focus-visible": { outline: `2px solid ${theme.palette.semantic["border/focus"]}`, outlineOffset: 2 },
    "& $avatar": { border: 0 },
    "& $name": { fontSize: 16, flex: 1, minWidth: 0, overflowWrap: "anywhere" },
    "& $nameArea": { flex: 1 },
    "& $divider": { margin: 0 },
    "& $commentLabelBox": {
      display: "inline-flex",
      border: 0,
      padding: "4px 8px",
      borderRadius: 4,
      backgroundColor: theme.palette.semantic["state/selected-bg"],
    },
    "& $commentLabel": { fontWeight: 700, lineHeight: 1.3 },
    "& $commentBody": { letterSpacing: 0, overflowWrap: "anywhere" },
    "& $radarTeaser, & $radarExpanded, & $ctaWrapper, & $companyName": { marginTop: 0 },
    "& $toggleLinkText": { fontWeight: 500, letterSpacing: 0, lineHeight: 1.3 },
    "& $toggleLinkIcon": { fontSize: 16 },
    "& $toggleLink": {
      background: "transparent",
      border: 0,
      padding: 0,
      width: "100%",
      fontFamily: "inherit",
      "&:focus-visible": { outline: `2px solid ${theme.palette.semantic["border/focus"]}`, outlineOffset: -2 },
    },
    "& $radarExpanded $toggleLink": { marginTop: 16 },
    "& $radarTeaser > div:first-child": {
      width: 260,
      maxWidth: "100%",
      marginLeft: "min(21px, calc((100% - 260px) / 2))",
    },
    "& $companyName": { overflowWrap: "anywhere" },
  },
  restoredIntroduction: {
    marginBottom: 0,
    "& $companyName": { marginTop: 4, overflowWrap: "anywhere" },
    "& $toggleLink": {
      background: "transparent",
      border: 0,
      padding: 0,
      width: "100%",
      fontFamily: "inherit",
      "&:focus-visible": { outline: `2px solid ${theme.palette.semantic["border/focus"]}`, outlineOffset: -2 },
    },
  },
  search: {
    "& $headerGroup": { gap: 4 },
    "& $commentLabelBox": { marginBottom: 8 },
    "& $headerRow": { alignItems: "center", gap: 16 },
    "& $name": { lineHeight: 1.5 },
    "& $companyName": { marginTop: 4, fontSize: 14 },
    "& $companyLogo": { width: 96, maxWidth: "100%" },
    [theme.breakpoints.up("md")]: { "& $commentBody": { fontSize: 16 }, "& $companyName": { fontSize: 14 } },
  },
  commentRow: { display: "flex", alignItems: "center", gap: 12, "& > div": { flex: 1, minWidth: 0 } },
}));

/**
 * 方針メモ（今回はリファクタしない）:
 * showRadar / showBadges / showCtaArrow / onClickCard / expandLabel と表示フラグが増え続けており、
 * isExpanded / onToggleExpand が showRadar=false では意味を持たないという制約も型で表現できていない。
 * 呼び出し元が3画面目に増える時点で、画面ごとの variant（判別可能ユニオン）へ整理する。
 * 現状は呼び出し元が2画面（診断結果・検索一覧）に留まるため、フラグのまま据え置く。
 */
interface CompatibilityAgentCardProps {
  agent: Agent;
  /** レーダーを展開表示しているか。showRadar が false のときは展開UI自体を出さないため意味を持たない（省略可） */
  isExpanded?: boolean;
  /** 展開リンク押下時のハンドラ。showRadar が false のときは展開UIを出さないため呼ばれない（省略可） */
  onToggleExpand?: () => void;
  /**
   * CTA ボタンの文言。
   * agent.is_matched が true のときはこの値は無視され、disabled の「マッチング済み」表示になる。
   */
  ctaLabel: string;
  /** CTA ボタン押下時のハンドラ。agent.is_matched が true のときは CTA が disabled のため呼ばれない */
  onClickCta: () => void;
  /** 相性グラフの展開リンク文言。画面ごとに文脈が違うため差し替え可能にしている。showRadar が false のときは使われない */
  expandLabel?: string;
  /**
   * カード内に相性レーダーと展開UIを出すか。
   * false にすると MATCH スコアだけを残し、レーダー本体・展開リンク・ヘルプアイコンを一切描画しない。
   * レーダーは1枚ごとに recharts の ResponsiveContainer（ResizeObserver + SVG）を持つため、
   * 最大100件を並べる検索一覧では false にして描画コストを避け、相性グラフは詳細画面で見せる。
   * 既定 true は3件固定の診断結果画面など従来の呼び出し元を無変更で維持するため。
   */
  showRadar?: boolean;
  /** CTA 右端の矢印アイコンを出すか（詳細遷移の CTA でのみ使う） */
  showCtaArrow?: boolean;
  /** カード全体タップでの遷移。未指定ならカード全体はクリック不能（CTA・展開リンクのみ操作可）になる */
  onClickCard?: () => void;
  /**
   * 左上に認定バッジ・NEWバッジを出すか。
   * 検索一覧でのみ使う表示のため opt-in にして、既定（診断結果など他画面）では出さない。
   * 検索一覧は登録フローと同じ認定バッジを表示する。NEW は登録から1か月以内。
   * 旧バリアントの画像バッジは従来どおり recommendation_level === 1 が対象。
   */
  showBadges?: boolean;
  /** 紹介・検索一覧は以前の情報配置を使う。 */
  variant?: "legacy" | "introduction" | "search";
  /** APIが返した一覧の順位。スコアによる並び替えは行わない。 */
  rank?: number;
}

export const CompatibilityAgentCard: React.FC<CompatibilityAgentCardProps> = ({
  agent,
  isExpanded = false,
  onToggleExpand,
  ctaLabel,
  onClickCta,
  expandLabel = DEFAULT_EXPAND_LABEL,
  showCtaArrow = true,
  onClickCard,
  showBadges = false,
  showRadar = true,
  variant = "legacy",
  rank,
}) => {
  const classes = useStyles();
  const isSearch = variant === "search";
  const isIntroduction = variant === "introduction" || isSearch;
  const isRestoredIntroduction = variant === "introduction";
  const ToggleElement = isIntroduction ? "button" : "div";
  const comment = agent.propally_comment?.comment;
  const companyLogoUrl = agent.company?.company_logo_image?.image_url;
  // compatibility_radar が nil のカードはスコア・レーダー・展開UIを出さない(カード自体は表示する)。
  const hasRadar = hasCompatibilityRadarScores(agent.compatibility_radar?.scores);
  // レーダーと展開UIはひとまとまり。呼び出し元が showRadar={false} を指定した場合はスコアだけ残す
  const showsRadarSection = showRadar && hasRadar;
  const totalScore = agent.compatibility_radar?.total_score;
  const companyLogo = companyLogoUrl ? (
    <img src={companyLogoUrl} alt={agent.company?.name} className={classes.companyLogo} />
  ) : null;
  const showsNewBadge = showBadges && isWithinOneMonth(agent.created_at);
  const showsCertificationBadge = showBadges && (isSearch || agent.recommendation_level === 1);
  const showsSearchScore = hasRadar && typeof totalScore === "number" && Number.isFinite(totalScore);
  // agent.id は生成型上 optional。id が無いと送信しても API 側で 400 になるだけなので最初から押させない。
  // 「有効な ID か」の判定は呼び出し側と同じ述語を使う。基準がズレると
  // 「CTA は押せるのにハンドラ側で弾かれて何も起きない」死にボタンになる
  const isCtaDisabled = !hasUsableAgentId(agent);
  const handleClickCard = (!isSearch && isIntroduction && agent.is_matched) || isCtaDisabled ? undefined : onClickCard;

  // カード全体が遷移する場合、カード内の操作要素は親へクリックを伝播させない
  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand?.();
  };

  const cardClassName = [
    classes.card,
    handleClickCard ? classes.clickableCard : "",
    isSearch ? classes.introduction : "",
    isRestoredIntroduction ? classes.restoredIntroduction : "",
    showBadges ? classes.badgeAnchor : "",
    isSearch ? classes.search : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box
      className={cardClassName}
      onClick={handleClickCard}
      aria-disabled={isSearch && isCtaDisabled ? true : undefined}
      role={isIntroduction && handleClickCard ? "link" : undefined}
      tabIndex={isIntroduction && handleClickCard ? 0 : undefined}
      aria-label={isIntroduction && handleClickCard ? `${buildAgentName(agent)}の詳細を見る` : undefined}
      data-testid={isSearch ? "search-agent-card" : isIntroduction ? "introduction-agent-card" : undefined}
      onKeyDown={
        isIntroduction && handleClickCard
          ? (event) => {
              if (event.target === event.currentTarget && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                handleClickCard();
              }
            }
          : undefined
      }
    >
      {/* カード左上のバッジ（NEW → 認定の順に並べる） */}
      {!isSearch && (showsNewBadge || showsCertificationBadge) && (
        <Box className={classes.topLeftBar}>
          {showsNewBadge && <img src={NewIcon} alt="New" />}
          {showsCertificationBadge && <img src={CertificationIcon} alt="Certified" />}
        </Box>
      )}
      <Box className={classes.headerGroup}>
        {isSearch && (showsNewBadge || showsCertificationBadge || showsSearchScore) && (
          <Box className={classes.searchMeta} data-testid="planner-card-meta">
            {(showsNewBadge || showsCertificationBadge) && (
              <Box className={classes.searchBadges}>
                {showsNewBadge && <StatusBadge status="new" label="NEW" data-testid="planner-new-label" />}
                {showsCertificationBadge && <CertifiedPlannerBadge />}
              </Box>
            )}
            {showsSearchScore && (
              <Box className={classes.searchScore}>
                <Typography className={classes.searchScoreLabel}>相性スコア</Typography>
                <CompatibilityScoreDisplay score={totalScore} rank={rank} size="L" tone="default" />
              </Box>
            )}
          </Box>
        )}
        <Box className={classes.headerRow}>
          <img
            src={agent.agent_profile_image?.image_url || NoImagePath}
            alt={buildAgentName(agent)}
            className={classes.avatar}
          />
          <Box className={classes.nameArea}>
            {isSearch ? companyLogo : isIntroduction ? (
              (companyLogo || (hasRadar && typeof totalScore === "number")) && (
                <Box className={classes.companyLogoScoreRow}>
                  {companyLogo}
                  {hasRadar && typeof totalScore === "number" && (
                    <CompatibilityScoreDisplay score={totalScore} rank={rank} size="L" />
                  )}
                </Box>
              )
            ) : companyLogo}
            <Typography className={classes.name}>{buildAgentName(agent)}</Typography>
            {isIntroduction && agent.company?.name && (
              <Typography className={classes.companyName}>{agent.company.name}</Typography>
            )}
            {!isIntroduction && <AgentBeginnerMessage message={agent.beginner_message} variant="card" />}
          </Box>
          {!isIntroduction && hasRadar && typeof totalScore === "number" && (
            <Box className={classes.matchScoreArea}>
              <Box className={classes.matchScoreBox}>
                <Typography className={classes.matchScoreValue}>{totalScore.toFixed(1)}</Typography>
                <Typography className={classes.matchScoreLabel}>MATCH</Typography>
              </Box>
              {/* ヘルプアイコンは展開トリガーなので、展開UIを出さないときは描画しない */}
              {showsRadarSection && !isExpanded && (
                <HelpOutlineIcon className={classes.helpIcon} onClick={handleToggleExpand} />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {comment && (
        <>
          <Box className={classes.divider} />
          <Box className={isSearch ? classes.commentRow : undefined}>
            <Box>
              <Box className={classes.commentLabelBox}>
                <Typography className={classes.commentLabel}>プロパリーからのコメント</Typography>
              </Box>
              {/* 展開UIが無いカード（レーダーなし・showRadar=false）は続きを読む手段がないため常に全文表示 */}
              <Typography
                className={
                  showsRadarSection && !isExpanded
                    ? `${classes.commentBody} ${classes.commentBodyClamped}`
                    : classes.commentBody
                }
              >
                {comment}
              </Typography>
            </Box>
            {isSearch && <Chevron direction="forward" style={{ color: theme.palette.semantic["icon/muted"] }} />}
          </Box>
        </>
      )}

      {showsRadarSection &&
        (isExpanded ? (
          <Box className={classes.radarExpanded}>
            <CompatibilityRadarChart
              scores={agent.compatibility_radar?.scores}
              size="full"
              variant={isSearch ? "introduction" : "legacy"}
            />
            <Box mt={2}>
              <CompatibilityRadarInfoButton variant="row" />
            </Box>
            <ToggleElement
              type={isIntroduction ? "button" : undefined}
              aria-expanded={isIntroduction ? true : undefined}
              className={classes.toggleLink}
              onClick={handleToggleExpand}
            >
              <Typography component="span" className={classes.toggleLinkText}>
                閉じる
              </Typography>
              <ArrowDropUpIcon className={classes.toggleLinkIcon} />
            </ToggleElement>
          </Box>
        ) : (
          <Box className={classes.radarTeaser}>
            <CompatibilityRadarChart
              scores={agent.compatibility_radar?.scores}
              size="full"
              hideLegend
              variant={isSearch ? "introduction" : "legacy"}
            />
            <Box className={classes.radarTeaserFade} />
            <Box className={classes.radarTeaserLink}>
              <ToggleElement
                type={isIntroduction ? "button" : undefined}
                aria-expanded={isIntroduction ? false : undefined}
                className={classes.toggleLink}
                onClick={handleToggleExpand}
              >
                <Typography component="span" className={classes.toggleLinkText}>
                  {expandLabel}
                </Typography>
                <ArrowDropDownIcon className={classes.toggleLinkIcon} />
              </ToggleElement>
            </Box>
          </Box>
        ))}

      {!isSearch && <Box className={classes.ctaWrapper}>
        {agent.is_matched ? (
          <CustomButton
            customVariant={isIntroduction ? undefined : "filled"}
            tone={isIntroduction ? "trust" : undefined}
            buttonSize={isIntroduction ? "L" : undefined}
            fullWidth={isIntroduction ? true : undefined}
            disabled
          >
            <Box component="span" className={classes.buttonText}>
              マッチング済み
            </Box>
          </CustomButton>
        ) : (
          <CustomButton
            customVariant={isIntroduction ? undefined : "filled"}
            tone={isIntroduction ? "trust" : undefined}
            buttonSize={isIntroduction ? "L" : undefined}
            fullWidth={isIntroduction ? true : undefined}
            disabled={isCtaDisabled}
            onClick={(e) => {
              e.stopPropagation();
              onClickCta();
            }}
          >
            {showCtaArrow && (!isIntroduction || isRestoredIntroduction) && <Box component="span" className={classes.buttonSpacer} />}
            <Box component="span" className={classes.buttonText}>
              {ctaLabel}
            </Box>
            {showCtaArrow && (!isIntroduction || isRestoredIntroduction) && <Chevron direction="forward" />}
          </CustomButton>
        )}
      </Box>}
      {!isIntroduction && agent.company?.name && (
        <Typography className={classes.companyName}>{agent.company.name}</Typography>
      )}
    </Box>
  );
};
